/* Imports */
import { h, render } from 'preact';
import { useEffect, useRef, useCallback, useState } from 'preact/hooks';
import Flickity from 'flickity';
import ImagesLoaded from 'imagesloaded';
import { animate, utils, stagger } from 'animejs';

/**
 * Preact Component for a single carousel cell.
 * @param {Object} props - Contains item data ({ id, title, rentalsAddress, rentalsNeighborhood, rentalsUrl: { url }, rentalsThumbnail: [{ url, alt }] })
 */
const CarouselCell = ({ item, index }) => {
    const imageUrl = item.rentalsThumbnail && item.rentalsThumbnail.length > 0 ? item.rentalsThumbnail[0].url : null;
    const imageAlt = item.rentalsThumbnail && item.rentalsThumbnail.length > 0 ? item.rentalsThumbnail[0].alt : '';
    const linkUrl = item.rentalsUrl && item.rentalsUrl.url ? item.rentalsUrl.url : null;

    const content = (
        <div class="jdl-rentals-carousel-item-wrapper" data-item-id={item.id}>
            {imageUrl ? (
                    <div className="jdl-rentals-carousel-item-image-wrapper">
                        <div className="jdl-rentals-carousel-item-image-container">
                            <img className="jdl-rentals-carousel-item-image" src={imageUrl} alt={imageAlt}/>
                        </div>
                    </div>
            ) : (
                <div className="jdl-rentals-carousel-item-image-wrapper">
                    <div className="jdl-rentals-carousel-item-image-container"></div>
                </div>
            )}
            {item.title && <h3 class="jdl-rentals-carousel-item-heading">{item.title}</h3>}
            {item.rentalsNeighborhood && <p class="jdl-rentals-carousel-item-text">{item.rentalsNeighborhood}</p>}
        </div>
    );

    return (
        // Apply style for CSS custom property --item-index
        <div class="jdl-rentals-carousel-item">
            <div class="jdl-rentals-carousel-item-frame">
                {linkUrl ? (
                    <a class="jdl-rentals-carousel-item-link" href={linkUrl} target="_blank" rel="noopener noreferrer"
                       data-external="">
                        {content}
                    </a>
                ) : (
                    content
                )}
            </div>
        </div>
    );
};

/**
 * Preact Component for the entire carousel system including external controls.
 * @param {Object} props - Contains carouselData
 */
const JdlCarouselSystem = ({carouselData}) => {
    const flickityRef = useRef(null);
    const prevButtonRef = useRef(null);
    const nextButtonRef = useRef(null);
    const carouselCellsRef = useRef([]); // Ref to hold references to carousel cells

    let flkty = null;
    let animation = null;

    const [isContentOpen, setIsContentOpen] = useState(false); // State to control content visibility

    // useCallback to memoize closeContent and openContent functions
    // This helps prevent unnecessary re-registrations of event listeners in subsequent effects
    const closeContent = useCallback(() => {
        if (isContentOpen) {
            if (animation) {
                animation.reverse(); // Reverse the animation
                animation.play();
            }

            setIsContentOpen(false);
        }
    }, [isContentOpen]); // Dependencies for closeContent: it needs to know the current isContentOpen state

    const openContent = useCallback(() => {
        if (!isContentOpen) {
            setIsContentOpen(true);
            // Animation will be triggered by useEffect when isContentOpen becomes true
        }
    }, [isContentOpen]); // Dependencies for openContent

    const toggleContent = useCallback(() => {
        if (isContentOpen) {
            closeContent();
        } else {
            openContent();
        }
    }, [isContentOpen, closeContent, openContent]); // Dependencies for toggleContent


    // Effect hook to initialize Flickity after the component mounts
    useEffect(() => {
        // Delay before starting item animations (after jdl-content appears)
        const initialDelay = 400; // First item animates after 400ms

        if (flickityRef.current && carouselData && Array.isArray(carouselData) && carouselData.length > 0) {
            const initFlickity = () => {
                flkty = new Flickity(flickityRef.current, {
                    cellAlign: 'left',
                    cellSelector: '.jdl-rentals-carousel-item',
                    contain: true,
                    draggable: true,
                    lazyLoad: false,
                    pageDots: false,
                    prevNextButtons: false,
                    wrapAround: false,
                });

                // Attach custom buttons
                if (prevButtonRef.current) {
                    prevButtonRef.current.addEventListener('click', () => flkty.previous());
                }
                if (nextButtonRef.current) {
                    nextButtonRef.current.addEventListener('click', () => flkty.next());
                }

                console.log('Flickity carousel initialized!');
            };

            // Load images before init carousel
            ImagesLoaded(flickityRef.current, initFlickity);
        }

        // Anime.js Animation Management
        if (isContentOpen) {
            // Collect all carousel-cell elements for animation
            const targets = flickityRef.current ? flickityRef.current.querySelectorAll('.jdl-rentals-carousel-item-frame') : null;

            if (targets.length > 0) {
                // Ensure initial state for animation (before animation starts)
                utils.set(targets, { opacity: 0 });

                animation = animate(targets,
                {
                    opacity: 1,
                    delay: stagger(100, { start: (initialDelay - 100) }),
                    duration: 400, // Duration for each item's fade
                    easing: 'easeOutQuad',
                    autoplay: false, // We'll play it manually
                    direction: 'normal', // Default direction for playing
                });
                animation.play(); // Start the animation
            }
        } else {
            // Content is closing, reset items to hidden state without animation
            setTimeout(() => {
                utils.set('.jdl-rentals-carousel-item-frame', { opacity: 0 });

                if (animation) {
                    animation.reset(); // Reset animation to its initial state
                    animation = null; // Clear animation instance
                }
            }, initialDelay);
        }

        // Cleanup: Destroy Flickity and reset/remove Anime.js animation on component unmount
        return () => {
            if (flkty) {
                flkty.destroy();
                //flkty = null;
            }
            // if (animation) {
            //     animation.reset();
            //     animation = null;
            // }
            // Clean up event listeners for custom buttons
            if (prevButtonRef.current) {
                prevButtonRef.current.removeEventListener('click', () => flkty?.previous()); // Use optional chaining for safety
            }
            if (nextButtonRef.current) {
                nextButtonRef.current.removeEventListener('click', () => flkty?.next());
            }
        };
    }, [isContentOpen, carouselData]); // Re-run effect if carouselData changes

    // Effect for global event listeners (Escape key, custom close event)
    useEffect(() => {
        // Handle Escape key press
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                console.log('Escape click');
                closeContent(); // Use the memoized closeContent
            }
        };

        // Handle custom event from external JS
        const handleExternalCloseEvent = () => {
            closeContent(); // Use the memoized closeContent
        };

        window.addEventListener('keydown', handleEscapeKey);
        document.addEventListener('closeJdlCarousel', handleExternalCloseEvent); // Listen for custom event

        // Cleanup listeners on component unmount
        return () => {
            window.removeEventListener('keydown', handleEscapeKey);
            document.removeEventListener('closeJdlCarousel', handleExternalCloseEvent);
        };
    }, [closeContent]); // Dependency: closeContent must be stable (memoized with useCallback)


    return (
        <>
            <button
                class={`jdl-rentals-btn ${isContentOpen ? 'jdl-rentals-btn-expanded' : ''}`}
                onClick={toggleContent}
                aria-expanded={isContentOpen}
            >
                <span
                    class="jdl-rentals-btn-arrow"
                    aria-hidden="true"
                >
                    <svg class="jdl-rentals-btn-arrow-icon" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 13 13" role="presentation">
                        <path d="M0.890001 0.890212L0.89 13.0002L13 13.0002L13 11.7502L3.03 11.7502L13 1.78021L13 0.890213L12.11 0.890213L2.14 10.8702L2.14 0.890212L0.890001 0.890212Z"/>
                    </svg>
                </span>

                <span class="jdl-rentals-btn-text-wrapper">
                    <span class="jdl-rentals-container">
                        <span class="jdl-rentals-btn-text">View more rental residences by JDL</span>
                    </span>
                </span>

                <span class="jdl-rentals-btn-logo-wrapper">
                    <span class="jdl-rentals-btn-logo">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 66 66" aria-label="JDL Logo" role="presentation">
                            <path d="M62.5352 39.2223C64.516 39.2876 66.0661 40.9553 65.9961 42.9352C65.9262 44.9167 64.2657 46.4696 62.2849 46.3997C60.3523 46.3345 58.8193 44.7474 58.8193 42.8141C58.8566 40.797 60.5171 39.1851 62.5306 39.2208H62.5337L62.5352 39.2223ZM35.476 33.4659C35.476 42.8002 30.8163 45.5037 27.7068 45.5037C23.498 45.5037 22.859 41.4787 22.7207 40.4786C22.4999 38.0468 22.4159 35.6011 22.475 33.1584H22.4424C22.4424 31.8509 22.4424 27.7606 22.7689 25.9733C22.7953 23.3148 24.9611 21.1796 27.6166 21.1982C30.1354 21.3442 32.4022 22.7697 33.6305 24.9779C35.0282 27.5774 35.6657 30.5232 35.4791 33.4674M57.0826 46.0969V44.867H51.7373V0H42.4647V1.21279H46.2568V44.867H41.0685V44.881H40.9441V0H31.872V1.21279H35.6595V25.582C34.5742 23.5927 32.3991 19.8146 27.2886 19.8146C22.178 19.8146 16.0429 24.481 16.0429 33.3898C16.0429 42.2986 22.178 46.8267 27.413 46.8267C32.6479 46.8267 34.6364 42.8172 35.6595 40.6044V46.0984H57.0842L57.0826 46.0969ZM11.6896 6.57795C13.6921 6.55155 15.3448 8.13858 15.4039 10.1418C15.2718 12.1962 13.4978 13.7569 11.4439 13.6249C9.57352 13.5037 8.08094 12.0099 7.96433 10.1418C8.00476 8.11995 9.67303 6.51273 11.6896 6.54844M5.21394 20.574H14.2378V49.2974C14.2378 52.7649 14.2378 58.3506 13.1961 60.8849C11.9756 63.9425 9.0309 65.9612 5.74101 66.0016C3.13055 66.0016 0.0241198 64.6024 0.0241198 61.3352C-0.173336 59.9656 0.771964 58.6953 2.14016 58.5012L2.41536 58.4748C3.74313 58.5478 4.76151 59.6829 4.68843 61.0122L4.68532 61.0712C4.71797 61.4485 4.71797 61.829 4.68532 62.2063L4.56094 62.8912C4.36659 63.7297 4.88744 64.5652 5.72391 64.7546C5.89182 64.795 6.0644 64.8058 6.23698 64.7872C6.97705 64.7763 7.68136 64.4611 8.18044 63.9161C9.04956 62.8617 9.04956 61.4439 9.04956 58.8475V21.7712H5.21394V20.574Z"/>
                        </svg>
                    </span>
                </span>
            </button>

            {/* Main content area, potentially hidden/shown by a store */}
            <div
                class={`jdl-rentals-content ${isContentOpen ? 'jdl-rentals-content-show' : ''}`}
                aria-hidden={!isContentOpen}
            >
                <div class="jdl-rentals-content-wrapper">
                    <div class="jdl-rentals-container jdl-rentals-content-hdr">
                        <div class="jdl-rentals-content-hdr-text">View more rental residences by JDL</div>
                    </div>

                    <div class="jdl-rentals-carousel-wrapper">
                        <div class="jdl-rentals-container">
                            <div class="jdl-rentals-carousel" aria-live="polite">
                                <div class="flickity-carousel" ref={flickityRef}> {/* Reference for Flickity */}
                                    {carouselData && Array.isArray(carouselData) && carouselData.map((item, index) => (
                                        <CarouselCell key={item.id || item.title || Math.random()} item={item} index={index} />
                                    ))}
                                </div>

                                <div class="jdl-rentals-carousel-controls">
                                    <div class="jdl-rentals-container jdl-rentals-carousel-controls-container">
                                        <div class="jdl-rentals-carousel-controls-grid">
                                            <div class="jdl-rentals-carousel-controls-grid-spacer"></div>

                                            <div class="jdl-rentals-carousel-arrows-container">
                                                <div class="jdl-rentals-carousel-arrows">
                                                    <button class="jdl-rentals-carousel-arrow jdl-rentals-carousel-button-prev" type="button" aria-label="Go to previous slide" ref={prevButtonRef}>
                                                        <svg class="jdl-rentals-carousel-arrow-icon" fill="currentColor" viewBox="0 0 9 14" xmlns="http://www.w3.org/2000/svg" role="presentation">
                                                            <path d="M7.348 14H9L2 7L9 0H7.348L0.348 7L7.348 14Z"/>
                                                        </svg>
                                                    </button>

                                                    <button class="jdl-rentals-carousel-arrow jdl-rentals-carousel-button-next" type="button" aria-label="Go to next slide" ref={nextButtonRef}>
                                                        <svg class="jdl-rentals-carousel-arrow-icon" fill="currentColor" viewBox="0 0 9 14" xmlns="http://www.w3.org/2000/svg" role="presentation">
                                                            <path d="M1.652 0H0L7 7L0 14H1.652L8.652 7L1.652 0Z"/>
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>

                                            <div class="jdl-rentals-carousel-controls-grid-spacer"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="jdl-rentals-container jdl-rentals-close-btn-wrapper">
                        <button
                            class="jdl-rentals-close-btn"
                            aria-label="Close popover"
                            onClick={toggleContent}
                        >
                            <svg class="jdl-rentals-close-btn-icon-mobile" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 18" role="presentation">
                                <path d="M.213 7.107v1.77l6.38-6.38v22.61h1.24V2.497l6.38 6.38v-1.77l-7-7-7 7Z"/>
                            </svg>

                            <svg class="jdl-rentals-close-btn-icon-desktop" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 13 13" role="presentation">
                                <path d="M12.9967 12.9998V0.889832H0.886719V2.13983H10.8567L0.886719 12.1098V12.9998H1.77672L11.7467 3.01983V12.9998H12.9967Z"/>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};


/**
 * Renders and initializes a Flickity carousel using Preact.
 * @param {HTMLElement} targetElement The element to append the carousel to.
 * @param {Object} carouselData An object containing an array with carousel item details.
 * Expected structure: [{ id, title, rentalsAddress, rentalsNeighborhood, rentalsUrl: { url }, rentalsThumbnail: [{ url, alt }] }]
 */
export function createCarousel(targetElement, carouselData) {
    if (!targetElement) {
        console.error('Target element not found for carousel injection.');
        return;
    }

    // Render the Preact component into the target element
    render(<JdlCarouselSystem carouselData={carouselData} />, targetElement);
}
